import { useEffect, useState } from "react";
import { BookOpen, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import Markdown from "react-markdown";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { manualUrl } from "@/components/HelpButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const btn = "btn-brutal px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50";
const field = "w-full border-2 border-foreground bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary";

export default function ChapterDraftDialog({ item, onClose, onAdded }) {
  const [chapters, setChapters] = useState([]);
  const [chapterId, setChapterId] = useState("");
  const [notes, setNotes] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!item) return;
    setDraft(null); setNotes(""); setBusy(false);
    api.get("/admin/help/chapters").then((r) => {
      setChapters(r.data.chapters);
      const ids = r.data.chapters.map((c) => c.id);
      setChapterId(item.section?.id && ids.includes(item.section.id) ? item.section.id : (ids[0] || ""));
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [item]);

  const generate = async () => {
    setBusy(true);
    try { setDraft((await api.post("/admin/help/chapter/draft", { question: item.question, notes, chapter_id: chapterId })).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/help/chapter", { chapter_id: chapterId, title: draft.title, anchor: draft.anchor, markdown: draft.markdown, session_id: item.session_id, index: item.index, question: item.question || "" });
      toast.success(`Paragraaf toegevoegd aan "${data.chapter_title}".`, { action: { label: "Bekijk", onClick: () => window.open(manualUrl(data.anchor), "_blank") } });
      onAdded?.(data); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const chapter = chapters.find((c) => c.id === chapterId);
  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl border-2 border-foreground rounded-none heavy-shadow max-h-[85vh] overflow-y-auto" data-testid="chapter-draft-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight pr-8 inline-flex items-center gap-2"><BookOpen className="w-5 h-5" /> Kennisgat vullen in de handleiding</DialogTitle>
          <DialogDescription>Jij geeft het antwoord in steekwoorden; de AI schrijft er een paragraaf in handleidingsstijl van. Die komt als sub-kopje onderaan het gekozen hoofdstuk en het kennisgat wordt afgehandeld.</DialogDescription>
        </DialogHeader>
        {item && (
          <div className="text-xs bg-muted/40 border border-border p-2" data-testid="chapter-draft-source">
            <span className="font-display uppercase tracking-widest text-[10px] text-muted-foreground">Vraag uit de chat</span>
            <div className="mt-0.5">“{item.question}”</div>
          </div>
        )}
        {!draft ? (
          <div className="space-y-3">
            <label className="block">
              <span className="font-display uppercase text-[10px] tracking-widest">Hoofdstuk</span>
              <select value={chapterId} onChange={(e) => setChapterId(e.target.value)} className={field} data-testid="chapter-draft-chapter">
                {chapters.map((c) => <option key={c.id} value={c.id}>{"\u00a0\u00a0".repeat(Math.max(c.level - 1, 0))}{c.title}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="font-display uppercase text-[10px] tracking-widest">Het juiste antwoord (steekwoorden)</span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={5} maxLength={4000} data-testid="chapter-draft-notes"
                placeholder="Bijv.: geen bankkoppeling; kas- en banksaldi per boekjaar handmatig invoeren bij Voorgaande jaren; Excel-import mogelijk" className={field} />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className={`${btn} bg-background text-foreground`} data-testid="chapter-draft-cancel">Annuleren</button>
              <button onClick={generate} disabled={busy || notes.trim().length < 3 || !chapterId} className={`${btn} bg-secondary text-secondary-foreground`} data-testid="chapter-draft-generate">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Laat de AI schrijven
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-[11px] text-muted-foreground">Komt in <b>{chapter?.title}</b> als {"#".repeat(draft.level)}-kopje · anker <code>#{draft.anchor}</code></div>
            <label className="block">
              <span className="font-display uppercase text-[10px] tracking-widest">Kop</span>
              <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} maxLength={120} className={`${field} font-bold`} data-testid="chapter-draft-title" />
            </label>
            <label className="block">
              <span className="font-display uppercase text-[10px] tracking-widest">Tekst (Markdown)</span>
              <textarea value={draft.markdown} onChange={(e) => setDraft({ ...draft, markdown: e.target.value })} rows={8} maxLength={8000} className={field} data-testid="chapter-draft-markdown" />
            </label>
            <div className="border-2 border-dashed border-border p-3 text-sm" data-testid="chapter-draft-preview">
              <div className="font-display uppercase text-[10px] tracking-widest text-muted-foreground mb-1">Voorbeeld in de handleiding</div>
              <p className="font-display font-bold uppercase tracking-wide">{draft.title}</p>
              <div className="prose prose-sm max-w-none"><Markdown>{draft.markdown}</Markdown></div>
            </div>
            <div className="flex justify-between gap-2 pt-1 flex-wrap">
              <button onClick={() => setDraft(null)} className={`${btn} bg-background text-foreground`} data-testid="chapter-draft-back">← Steekwoorden aanpassen</button>
              <button onClick={save} disabled={busy || draft.title.trim().length < 3 || draft.markdown.trim().length < 3} className={`${btn} bg-primary text-primary-foreground`} data-testid="chapter-draft-save">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Opslaan in handleiding
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
